import { useCallback, useMemo } from 'react';
import {
  EnumCutsheetAnimalSpecies,
  EnumCutsheetInspectionLevels,
  PrimalCutBlockerBlockedBy,
  ProcessorCapability,
  ProcessorSettings,
  usePrimalCutBlockerManyQuery,
} from 'lib/graphql';
import { CutsGroup } from '../types';

export type BuildCutsDataResult = {
  isReady: boolean;
  cutsByPrimalName: CutsGroup[];
  messages: {
    missingSelection?: string;
  };
  // Example callback placeholders for future behaviors
  refresh: () => void;
  reset: () => void;
};

export function useBuildCutsData(
  animalSpecies: EnumCutsheetAnimalSpecies,
  capabilities: ProcessorCapability[],
  inspectionLevel: EnumCutsheetInspectionLevels,
  settings: ProcessorSettings,
): BuildCutsDataResult {
  // find the processor capability that matches the animal species and inspection level
  const cutsByAnimalSpeciesAndInspectionLevel = useMemo(
    () =>
      capabilities.find(
        (capability) =>
          (capability.animalSpecies as unknown as EnumCutsheetAnimalSpecies) ===
            animalSpecies &&
          capability.inspectionLevel === (inspectionLevel as unknown as string),
      ),
    [animalSpecies, inspectionLevel, capabilities],
  );

  // Get enabled cut IDs from the capability's primalCuts field
  const enabledCutIds = useMemo(() => {
    const cutIds = cutsByAnimalSpeciesAndInspectionLevel?.primalCuts || [];
    return new Set(
      cutIds.map((cutId) => (typeof cutId === 'string' ? cutId : cutId)),
    );
  }, [cutsByAnimalSpeciesAndInspectionLevel]);

  // filter out deleted cuts and only include enabled cuts
  // The GraphQL type allows null elements in the array, so we use a type guard
  // predicate to narrow to PrimalCut[] before filtering.
  const activeCuts = useMemo(
    () =>
      (cutsByAnimalSpeciesAndInspectionLevel?.cuts ?? [])
        .filter((cut): cut is NonNullable<typeof cut> => cut != null)
        .filter((cut) => !cut.isDeleted && enabledCutIds.has(cut._id)),
    [cutsByAnimalSpeciesAndInspectionLevel, enabledCutIds],
  );

  // Build a map of primal names to primalGroup order and imageUrl
  // This ensures wizard ordering matches the capabilities page
  const primalGroupOrderMap = useMemo(() => {
    const map = new Map<string, number>();
    const primalGroups = cutsByAnimalSpeciesAndInspectionLevel?.primalGroups;

    if (primalGroups && Array.isArray(primalGroups)) {
      for (const group of primalGroups) {
        if (!group) continue; // GraphQL arrays may contain null elements
        if (!group.primals) continue;
        for (const primal of group.primals) {
          if (!primal) continue; // primal may be null
          if (!primal.name) continue;
          const normalizedName = primal.name.trim().toLowerCase();
          const groupId = (primal._id as string) ?? normalizedName;
          // Use the primalGroup's order (not the primal's order)
          map.set(groupId, group.order);
        }
      }
    }

    return map;
  }, [cutsByAnimalSpeciesAndInspectionLevel]);

  // Build a map of primal IDs to imageUrls
  const primalImageUrlMap = useMemo(() => {
    const map = new Map<string, string>();
    const primalGroups = cutsByAnimalSpeciesAndInspectionLevel?.primalGroups;

    if (primalGroups && Array.isArray(primalGroups)) {
      for (const group of primalGroups) {
        if (!group) continue; // GraphQL arrays may contain null elements
        if (!group.primals) continue;
        for (const primal of group.primals) {
          if (!primal) continue; // primal may be null
          if (!primal._id) continue;
          const primalId = primal._id as string;
          if (primal.imageUrl) {
            map.set(primalId, primal.imageUrl);
          }
        }
      }
    }

    return map;
  }, [cutsByAnimalSpeciesAndInspectionLevel]);

  // primal cut blockers from the API (async)
  const { data: blockers } = usePrimalCutBlockerManyQuery({
    variables: {
      filter: {
        vendor: settings?.vendor?._id,
      },
      limit: 1000,
    },
  });

  // create a set of blockers for each cut as the data returns from the API
  // this prevents an empty array from being returned
  const blockersByCutId = useMemo(() => {
    const map = new Map<string, PrimalCutBlockerBlockedBy[]>();
    const rows = blockers?.primalCutBlockerMany ?? [];
    for (const row of rows) {
      const id = row?.primalCutId;
      if (!id) continue;
      // Only store the blockedBy array per cut ID
      map.set(id, (row?.blockedBy ?? []) as PrimalCutBlockerBlockedBy[]);
    }
    return map;
  }, [blockers]);

  // group cuts by primal name
  const cutsByPrimalName = useMemo<CutsGroup[]>(() => {
    if (!activeCuts || activeCuts.length === 0) return [];

    const byName = new Map<string, CutsGroup>();

    for (const cut of activeCuts) {
      const { primal } = cut;
      if (!primal) continue;

      const normalizedName = (primal.name || 'Uncategorized')
        .trim()
        .toLowerCase();

      const groupId = (primal._id as string) ?? normalizedName;

      let group = byName.get(groupId);
      if (!group) {
        // Look up primalGroup order from the map (if available)
        // Fall back to cut order if primalGroup order not found
        const groupOrder = primalGroupOrderMap.get(groupId) ?? cut.order;

        // Get imageUrl from the map
        const imageUrl = primalImageUrlMap.get(groupId) || '';

        group = {
          primalId: groupId, // keep first seen ID as there are duplicate primals with different IDs
          primalName: primal.name ?? 'Uncategorized',
          description: primal.description || '',
          imageUrl: imageUrl,
          order: groupOrder,
          cuts: [],
        };
        byName.set(groupId, group);
      }

      // Add blockedBy for this cut
      const cutWithBlockers = {
        ...cut,
        blockedBy: blockersByCutId.get(cut._id) ?? [],
      };

      group.cuts.push(cutWithBlockers);
    }

    console.log('cutsByPrimalName', byName);

    const groups = Array.from(byName.values());
    // Sort groups by order, with undefined values last (matching lodash orderBy behavior)
    groups.sort((a, b) => {
      const orderA = a.order ?? Infinity;
      const orderB = b.order ?? Infinity;
      return orderA - orderB;
    });
    // Sort cuts within each group by order, with undefined values last
    groups.forEach((group) => {
      group.cuts.sort((a, b) => {
        const orderA = a.order ?? Infinity;
        const orderB = b.order ?? Infinity;
        return orderA - orderB;
      });
    });

    return groups;
  }, [activeCuts, blockersByCutId, primalGroupOrderMap, primalImageUrlMap]);

  // Whether inputs are sufficient to build the data
  const isReady = useMemo<boolean>(
    () =>
      // TODO: compute readiness based on required inputs
      false,
    [],
  );

  // UX messages for empty/not-ready states
  const messages = useMemo(
    () =>
      // TODO: set helpful messages depending on which inputs are missing
      ({ missingSelection: undefined as string | undefined }),
    [],
  );

  // Placeholders for future behaviors
  const refresh = useCallback(() => {
    // TODO: re-run data build (e.g., refetch or recompute)
  }, []);

  const reset = useCallback(() => {
    // TODO: reset internal state, if any
  }, []);

  return {
    isReady,
    cutsByPrimalName,
    messages,
    refresh,
    reset,
  };
}
